import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Alert,
} from '@mui/material';

import {
  ProjectDetailPageProps,
  CalculationParametersUpdate,
  Windpark,
} from '../../../types/index';
import { apiService } from '../../../services/api';
import { projectsApi } from '../../../services/api/projects';
import { useToast } from '../../../contexts/ToastContext';
import { usePageWarnings } from '../../../contexts/PageWarningsContext';
import { useCalculationPageData } from './hooks/useCalculationPageData';
import { useCalculationSliders } from './hooks/useCalculationSliders';
import PageTemplate from '../../layout/PageTemplate';
import ProfitAndLossSection from './sections/ProfitAndLossSection';
import TradeTaxSection from './sections/TradeTaxSection';
import CashflowSection from './sections/CashflowSection';
import KPISection from './sections/KPISection';
import MainParametersSection from './sections/MainParametersSection';
import EconomySection from './sections/EconomySection';
import LoadingOverlay from '../../common/feedback/LoadingOverlay';

interface CalculationPageProps extends ProjectDetailPageProps {
  canView: boolean;
  canEdit: boolean;
}

const CalculationPage: React.FC<CalculationPageProps> = ({
  project: initialProject,
  onBack,
  onTabChange,
  currentTab,
  canView,
  canEdit
}) => {
  // Daten-Hook: GET beim Mount, POST bei Neuberechnung
  const { guvData, cashflowData, kpis, isLoading, isCalculating, error, calculateAll } = useCalculationPageData(initialProject.id);
  const { showSuccess, showError } = useToast();
  const { setValidationsFromAPI } = usePageWarnings();

  // Lokaler State für Projekt-Daten (optimistisches Update für Kalkulationsparameter)
  const [project, setProject] = useState<Windpark>(initialProject);

  useEffect(() => {
    setProject(initialProject);
  }, [initialProject]);

  // Slider-Hook: Lädt EEG/OPEX/Financing und kapselt alle Slider-Handler
  const {
    eegTariff,
    opex,
    financing,
    handleZuschlagswertChange,
    handlePachtProzentChange,
    handleEkQuoteChange,
    handleLeaseTierPercentageChange,
  } = useCalculationSliders({
    projectId: initialProject.id,
    canEdit,
    onParameterSaved: calculateAll,
    onValidationsReceived: setValidationsFromAPI,
  });

  const strToNum = (value: string): number | undefined => {
    if (!value || value.trim() === '') return undefined;
    const cleanValue = value.replace(/[^\d,.-]/g, '').replace(',', '.');
    const num = parseFloat(cleanValue);
    return isNaN(num) ? undefined : num;
  };

  const handleParameterChange = useCallback((field: keyof CalculationParametersUpdate) => async (value: string) => {
    const numValue = strToNum(value);

    // Optimistisches Update
    setProject(prev => ({ ...prev, [field]: numValue }));

    try {
      if (field === 'laufzeit_jahre' && typeof numValue === 'number') {
        const response = await projectsApi.updateProjectLaufzeit(project.id, numValue);
        setProject(response.data);
        setValidationsFromAPI(response.validations);
        if (response.validations.warnings.length > 0) {
          showSuccess(`Gespeichert (${response.validations.warnings.length} Warnung${response.validations.warnings.length > 1 ? 'en' : ''})`);
        } else {
          showSuccess('Gespeichert');
        }
      } else {
        const updated = await apiService.updateProjectPartial(project.id, { [field]: numValue });
        setProject(updated);
        showSuccess('Gespeichert');
      }

      // Nach Speichern: Neuberechnung
      try {
        await calculateAll();
      } catch {
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Fehler beim Speichern';
      showError(errorMsg);
    }
  }, [project.id, showSuccess, showError, calculateAll, setValidationsFromAPI]);

  return (
    <PageTemplate
      isLoading={isLoading}
      error={error && guvData.length === 0 ? error : undefined}
    >
      {/* KPI-Kennzahlen (IRR, Min DSCR, Avg DSCR) */}
      <LoadingOverlay isLoading={isCalculating}>
        <KPISection kpis={kpis} />
      </LoadingOverlay>

      {/* Wirtschaftlichkeits-Chart: Ergebnis nach Steuern + Cashflow */}
      <LoadingOverlay isLoading={isCalculating}>
        <EconomySection guvData={guvData} cashflowData={cashflowData} />
      </LoadingOverlay>

      {/* Haupt-Parameter als moderne Slider */}
      <MainParametersSection
        eegTariff={eegTariff}
        opex={opex}
        financing={financing}
        canEdit={canEdit}
        onZuschlagswertChange={handleZuschlagswertChange}
        onPachtProzentChange={handlePachtProzentChange}
        onEkQuoteChange={handleEkQuoteChange}
        onLeaseTierPercentageChange={handleLeaseTierPercentageChange}
        projektLaufzeit={project.laufzeit_jahre!}
      />

      {/* Nicht-kritischer Fehler (Daten vorhanden aber Neuberechnung fehlgeschlagen) */}
      {error && guvData.length > 0 && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Cashflow-Berechnung */}
      <LoadingOverlay isLoading={isCalculating}>
        <CashflowSection data={cashflowData} />
      </LoadingOverlay>

      {/* GuV-Prognose */}
      <LoadingOverlay isLoading={isCalculating}>
        <Box sx={{ mt: 3 }}>
          <ProfitAndLossSection data={guvData} />
        </Box>
      </LoadingOverlay>

      {/* Gewerbesteuer-Berechnung Details */}
      <LoadingOverlay isLoading={isCalculating}>
        <Box sx={{ mt: 3 }}>
          <TradeTaxSection data={guvData} />
        </Box>
      </LoadingOverlay>
    </PageTemplate>
  );
};

export default CalculationPage;
